/**
 * `board.createdBy` — who made a board (Boards Redesign extras §2.8): the
 * owner avatar on the overview tile. Pure helpers, no React, no IPC.
 *
 * Written on create since 1.0.7 (New board modal, in-app AI, MCP). Boards made
 * earlier carry none; the owner approved filling it in (12.10.2026):
 *
 * - a PRIVATE board (personal or this-device scope) is the signed-in user's;
 * - a TEAM board is whoever the server says created or shared it in
 *   (`created_by` of GET /teams/:id/boards — teamBoardOwners.js).
 *
 * The fill-in is written once, as a normal board edit, when the board is open
 * and editable (useBoardCreatedByFill). Unknown → nothing, never a guess.
 */

const idOf = (value) => (value == null || value === '' ? null : String(value));

/**
 * The user id a record scope belongs to: `personal:42` / `device:42` → '42'.
 * `personal:local` (signed out), team scopes and anything else → null.
 */
export function userIdOfScope(scopeKey) {
  const match = /^(?:personal|device):(.+)$/.exec(String(scopeKey || ''));
  if (!match || match[1] === 'local') return null;
  return match[1];
}

/**
 * Who should be filled in as the board's creator, or null when nothing should
 * be written (it already has one, or nobody is known).
 *
 * `teamOwners` is the `{ boardId: userId }` map of the board's team.
 */
export function createdByFillFor(board, { myId, teamOwners } = {}) {
  if (!board?.id || idOf(board.createdBy)) return null;
  if (board.visibility === 'team') return idOf(teamOwners?.[board.id]);
  return idOf(myId);
}

/**
 * The owner to SHOW for a board before (or without) the write-back: its own
 * `createdBy`, else what the fill-in would write.
 */
export function effectiveCreatedBy(board, ctx) {
  return idOf(board?.createdBy) || createdByFillFor(board, ctx);
}

/** GET /teams/:id/boards → `{ boardId: userId }` (entries without one left out). */
export function ownersFromTeamBoards(list) {
  const out = {};
  for (const b of Array.isArray(list) ? list : []) {
    const id = idOf(b?.id);
    const by = idOf(b?.created_by ?? b?.createdBy);
    if (id && by) out[id] = by;
  }
  return out;
}
